import { Fragment, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronUp, Search, Mail, User, BarChart3, RefreshCcw, CheckCircle2, Calendar, FileText, HelpCircle } from 'lucide-react';
import api from '../services/api';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const formatDate = (value) => {
  if (!value) return '-';
  const normalized = String(value).slice(0, 10);
  const parts = normalized.split('-');
  if (parts.length !== 3) return value;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const getTodayIsoDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const HeaderHelp = ({ label, help }) => {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
      <span>{label}</span>
      {help ? (
        <Tooltip title={help} arrow>
          <IconButton
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{ p: 0.25, borderRadius: '10px', color: 'text.secondary' }}
          >
            <HelpCircle size={14} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

const RadioHelpLabel = ({ label, help }) => {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <span>{label}</span>
      {help ? (
        <Tooltip title={help} arrow>
          <IconButton size="small" onClick={(e) => e.stopPropagation()} sx={{ p: 0.25, borderRadius: '10px', color: 'text.secondary' }}>
            <HelpCircle size={14} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default function PraticheCarichiLavoro() {
  const { addJob } = useJob();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [referenceDate, setReferenceDate] = useState(getTodayIsoDate());
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportDetailMode, setReportDetailMode] = useState('issuer');
  const [issuerOptions, setIssuerOptions] = useState([]);
  const [excludeIssuers, setExcludeIssuers] = useState([]);
  const [excludedMeta, setExcludedMeta] = useState({ byIssuerId: {}, senzaCreditore: 0 });
  const [expandedLegalId, setExpandedLegalId] = useState(null);
  const [issuerDistributionByLegalId, setIssuerDistributionByLegalId] = useState({});
  const [issuerDistributionLoadingByLegalId, setIssuerDistributionLoadingByLegalId] = useState({});
  const [expandedIssuerKey, setExpandedIssuerKey] = useState(null);
  const [contabiliDistributionByIssuerKey, setContabiliDistributionByIssuerKey] = useState({});
  const [contabiliDistributionLoadingByIssuerKey, setContabiliDistributionLoadingByIssuerKey] = useState({});
  const [detailTab, setDetailTab] = useState('issuer');
  const [recessiTab, setRecessiTab] = useState('motivazioni');
  const [recessiMotivazioniByLegalId, setRecessiMotivazioniByLegalId] = useState({});
  const [recessiMotivazioniLoadingByLegalId, setRecessiMotivazioniLoadingByLegalId] = useState({});
  const [expandedMotivazioneKey, setExpandedMotivazioneKey] = useState(null);
  const [recessiMotivazioniDetailsByKey, setRecessiMotivazioniDetailsByKey] = useState({});
  const [recessiMotivazioniDetailsLoadingByKey, setRecessiMotivazioniDetailsLoadingByKey] = useState({});
  const [recessiTipologieByLegalId, setRecessiTipologieByLegalId] = useState({});
  const [recessiTipologieLoadingByLegalId, setRecessiTipologieLoadingByLegalId] = useState({});
  const [expandedTipologiaKey, setExpandedTipologiaKey] = useState(null);
  const [recessiTipologieDetailsByKey, setRecessiTipologieDetailsByKey] = useState({});
  const [recessiTipologieDetailsLoadingByKey, setRecessiTipologieDetailsLoadingByKey] = useState({});
  const [orderBy, setOrderBy] = useState('pratiche_totale');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [introOpen, setIntroOpen] = useState(true);

  const buildIssuerKey = (legalId, issuerId) => `${Number(legalId)}:${Number(issuerId)}`;
  const buildMotivazioneKey = (legalId, motivazione) => `${Number(legalId)}:${String(motivazione || '').trim().toLowerCase()}`;
  const buildTipologiaKey = (legalId, categoryId) => `${Number(legalId)}:${Number(categoryId)}`;

  const motivazioneLabel = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'lasciare') return 'Lasciare';
    if (normalized === 'recesso_nei_tempi') return 'Recesso nei tempi';
    if (normalized === 'perseguire') return 'Perseguire';
    return normalized || '-';
  };

  const handleStartLegalDetailReportJob = async (row) => {
    try {
      const legalId = Number(row?.legal_id);
      if (!Number.isFinite(legalId) || legalId <= 0) {
        toast.error('Legale non valido');
        return;
      }
      const payload = {
        filters: {
          reference_date: referenceDate || '',
          legal_id: legalId,
          legal_name: row?.name || '',
          legal_email: row?.email || '',
          exclude_issuer_ids: excludeIssuers.map((i) => i.id),
          exclude_issuer_names: excludeIssuers.map((i) => i.name)
        }
      };
      const res = await api.post('/reports/pratiche-carichi-lavoro-legal-detail', payload);
      if (res.data && res.data.jobId) {
        addJob(res.data.jobId, 'pratiche_carichi_lavoro_legal_detail_report', {
          meta: {
            referenceDate,
            legalId,
            legalName: row?.name || '',
            excludedIssuersCount: excludeIssuers.length
          }
        });
      }
    } catch (error) {
      console.error('Error starting legal detail report job:', error);
      toast.error("Errore durante l'avvio del report dettaglio legale");
    }
  };

  const handleStartCarichiLavoroReportJob = async ({ detailMode } = {}) => {
    try {
      setReportLoading(true);
      const selectedMode = String(detailMode || reportDetailMode || 'issuer');
      const includeIssuerDetail = selectedMode === 'issuer' || selectedMode === 'issuer_contabili';
      const includeContabiliDetail = selectedMode === 'issuer_contabili';
      const payload = {
        filters: {
          reference_date: referenceDate || '',
          legal_search: search || '',
          exclude_issuer_ids: excludeIssuers.map((i) => i.id),
          exclude_issuer_names: excludeIssuers.map((i) => i.name),
          include_details_issuers: includeIssuerDetail,
          include_details_contabili: includeContabiliDetail
        }
      };
      const res = await api.post('/reports/pratiche-carichi-lavoro', payload);
      if (res.data && res.data.jobId) {
        addJob(res.data.jobId, 'pratiche_carichi_lavoro_report', {
          meta: {
            referenceDate,
            legalSearch: search || '',
            excludedIssuersCount: excludeIssuers.length,
            detailMode: selectedMode
          }
        });
      }
    } catch (error) {
      console.error('Error starting carichi di lavoro report job:', error);
      toast.error("Errore durante l'avvio del report PDF");
    } finally {
      setReportLoading(false);
    }
  };

  const fetchContabiliDistribution = async (legalId, issuerId) => {
    const legalIdSafe = Number(legalId);
    const issuerIdSafe = Number(issuerId);
    if (!Number.isFinite(legalIdSafe) || legalIdSafe <= 0) return;
    if (!Number.isFinite(issuerIdSafe) || issuerIdSafe < 0) return;
    const key = buildIssuerKey(legalIdSafe, issuerIdSafe);
    try {
      setContabiliDistributionLoadingByIssuerKey((prev) => ({ ...prev, [key]: true }));
      const excludeIssuerIds = excludeIssuers.map((issuer) => issuer.id);
      const params = {
        legal_id: legalIdSafe,
        issuer_id: issuerIdSafe,
        reference_date: referenceDate || getTodayIsoDate()
      };
      if (excludeIssuerIds.length > 0) {
        params.exclude_issuer_ids = excludeIssuerIds.join(',');
      }
      const res = await api.get('/negotiation/monitoring/workloads/issuer-contabili-distribution', { params });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setContabiliDistributionByIssuerKey((prev) => ({ ...prev, [key]: items }));
    } catch (error) {
      console.error('Error fetching contabili distribution:', error);
      toast.error('Errore nel caricamento del dettaglio contabile');
    } finally {
      setContabiliDistributionLoadingByIssuerKey((prev) => ({ ...prev, [key]: false }));
    }
  };

  const fetchIssuerDistribution = async (legalId) => {
    try {
      const legalIdSafe = Number(legalId);
      if (!Number.isFinite(legalIdSafe) || legalIdSafe <= 0) return;
      setIssuerDistributionLoadingByLegalId((prev) => ({ ...prev, [legalIdSafe]: true }));
      const excludeIssuerIds = excludeIssuers.map((issuer) => issuer.id);
      const params = {
        legal_id: legalIdSafe,
        reference_date: referenceDate || getTodayIsoDate()
      };
      if (excludeIssuerIds.length > 0) {
        params.exclude_issuer_ids = excludeIssuerIds.join(',');
      }
      const res = await api.get('/negotiation/monitoring/workloads/issuer-distribution', { params });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setIssuerDistributionByLegalId((prev) => ({ ...prev, [legalIdSafe]: items }));
    } catch (error) {
      console.error('Error fetching issuer distribution:', error);
      toast.error('Errore nel caricamento della distribuzione creditori');
    } finally {
      const legalIdSafe = Number(legalId);
      setIssuerDistributionLoadingByLegalId((prev) => ({ ...prev, [legalIdSafe]: false }));
    }
  };

  const fetchRecessiMotivazioniDistribution = async (legalId) => {
    try {
      const legalIdSafe = Number(legalId);
      if (!Number.isFinite(legalIdSafe) || legalIdSafe <= 0) return;
      setRecessiMotivazioniLoadingByLegalId((prev) => ({ ...prev, [legalIdSafe]: true }));
      const excludeIssuerIds = excludeIssuers.map((issuer) => issuer.id);
      const params = {
        legal_id: legalIdSafe,
        reference_date: referenceDate || getTodayIsoDate()
      };
      if (excludeIssuerIds.length > 0) {
        params.exclude_issuer_ids = excludeIssuerIds.join(',');
      }
      const res = await api.get('/negotiation/monitoring/workloads/recessi-motivazioni', { params });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setRecessiMotivazioniByLegalId((prev) => ({ ...prev, [legalIdSafe]: items }));
    } catch (error) {
      console.error('Error fetching recessi motivazioni distribution:', error);
      toast.error('Errore nel caricamento del dettaglio recessi');
    } finally {
      const legalIdSafe = Number(legalId);
      setRecessiMotivazioniLoadingByLegalId((prev) => ({ ...prev, [legalIdSafe]: false }));
    }
  };

  const fetchRecessiMotivazioniDetails = async (legalId, motivazione) => {
    const legalIdSafe = Number(legalId);
    const motivazioneSafe = String(motivazione || '').trim().toLowerCase();
    if (!Number.isFinite(legalIdSafe) || legalIdSafe <= 0) return;
    if (!motivazioneSafe) return;
    const key = buildMotivazioneKey(legalIdSafe, motivazioneSafe);
    try {
      setRecessiMotivazioniDetailsLoadingByKey((prev) => ({ ...prev, [key]: true }));
      const excludeIssuerIds = excludeIssuers.map((issuer) => issuer.id);
      const params = {
        legal_id: legalIdSafe,
        reference_date: referenceDate || getTodayIsoDate(),
        motivazione: motivazioneSafe
      };
      if (excludeIssuerIds.length > 0) {
        params.exclude_issuer_ids = excludeIssuerIds.join(',');
      }
      const res = await api.get('/negotiation/monitoring/workloads/recessi-motivazioni/details', { params });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setRecessiMotivazioniDetailsByKey((prev) => ({ ...prev, [key]: items }));
    } catch (error) {
      console.error('Error fetching recessi motivazioni details:', error);
      toast.error('Errore nel caricamento del dettaglio recessi');
    } finally {
      setRecessiMotivazioniDetailsLoadingByKey((prev) => ({ ...prev, [key]: false }));
    }
  };

  const fetchRecessiTipologieDistribution = async (legalId) => {
    try {
      const legalIdSafe = Number(legalId);
      if (!Number.isFinite(legalIdSafe) || legalIdSafe <= 0) return;
      setRecessiTipologieLoadingByLegalId((prev) => ({ ...prev, [legalIdSafe]: true }));
      const excludeIssuerIds = excludeIssuers.map((issuer) => issuer.id);
      const params = {
        legal_id: legalIdSafe,
        reference_date: referenceDate || getTodayIsoDate()
      };
      if (excludeIssuerIds.length > 0) {
        params.exclude_issuer_ids = excludeIssuerIds.join(',');
      }
      const res = await api.get('/negotiation/monitoring/workloads/recessi-tipologie', { params });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setRecessiTipologieByLegalId((prev) => ({ ...prev, [legalIdSafe]: items }));
    } catch (error) {
      console.error('Error fetching recessi tipologie distribution:', error);
      toast.error('Errore nel caricamento del dettaglio recessi');
    } finally {
      const legalIdSafe = Number(legalId);
      setRecessiTipologieLoadingByLegalId((prev) => ({ ...prev, [legalIdSafe]: false }));
    }
  };

  const fetchRecessiTipologieDetails = async (legalId, categoryId) => {
    const legalIdSafe = Number(legalId);
    const categoryIdSafe = Number(categoryId);
    if (!Number.isFinite(legalIdSafe) || legalIdSafe <= 0) return;
    if (!Number.isFinite(categoryIdSafe) || categoryIdSafe < 0) return;
    const key = buildTipologiaKey(legalIdSafe, categoryIdSafe);
    try {
      setRecessiTipologieDetailsLoadingByKey((prev) => ({ ...prev, [key]: true }));
      const excludeIssuerIds = excludeIssuers.map((issuer) => issuer.id);
      const params = {
        legal_id: legalIdSafe,
        reference_date: referenceDate || getTodayIsoDate(),
        category_id: categoryIdSafe
      };
      if (excludeIssuerIds.length > 0) {
        params.exclude_issuer_ids = excludeIssuerIds.join(',');
      }
      const res = await api.get('/negotiation/monitoring/workloads/recessi-tipologie/details', { params });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setRecessiTipologieDetailsByKey((prev) => ({ ...prev, [key]: items }));
    } catch (error) {
      console.error('Error fetching recessi tipologie details:', error);
      toast.error('Errore nel caricamento del dettaglio recessi');
    } finally {
      setRecessiTipologieDetailsLoadingByKey((prev) => ({ ...prev, [key]: false }));
    }
  };

  const fetchData = async ({ dateOverride, excludeIssuerIdsOverride, legalSearchOverride } = {}) => {
    try {
      setLoading(true);
      setExpandedLegalId(null);
      setIssuerDistributionByLegalId({});
      setIssuerDistributionLoadingByLegalId({});
      setExpandedIssuerKey(null);
      setContabiliDistributionByIssuerKey({});
      setContabiliDistributionLoadingByIssuerKey({});
      setDetailTab('issuer');
      setRecessiTab('motivazioni');
      setRecessiMotivazioniByLegalId({});
      setRecessiMotivazioniLoadingByLegalId({});
      setExpandedMotivazioneKey(null);
      setRecessiMotivazioniDetailsByKey({});
      setRecessiMotivazioniDetailsLoadingByKey({});
      setRecessiTipologieByLegalId({});
      setRecessiTipologieLoadingByLegalId({});
      setExpandedTipologiaKey(null);
      setRecessiTipologieDetailsByKey({});
      setRecessiTipologieDetailsLoadingByKey({});
      const effectiveDate = dateOverride || referenceDate || getTodayIsoDate();
      const excludeIssuerIds =
        Array.isArray(excludeIssuerIdsOverride) ? excludeIssuerIdsOverride : excludeIssuers.map((issuer) => issuer.id);
      const params = { reference_date: effectiveDate };
      if (excludeIssuerIds.length > 0) {
        params.exclude_issuer_ids = excludeIssuerIds.join(',');
      }
      const legalSearch = String(legalSearchOverride ?? search ?? '').trim();
      if (legalSearch) {
        params.legal_search = legalSearch;
      }
      const res = await api.get('/negotiation/monitoring/workloads', {
        params
      });
      const data = res.data || {};
      setRows(Array.isArray(data.items) ? data.items : []);
      setReferenceDate(data.referenceDate || effectiveDate);
      const excluded = data.excluded || null;
      if (excluded && typeof excluded === 'object') {
        const issuers = Array.isArray(excluded.issuers) ? excluded.issuers : [];
        const byIssuerId = {};
        issuers.forEach((item) => {
          const id = Number(item?.issuer_id);
          if (Number.isFinite(id) && id > 0) {
            byIssuerId[id] = Number(item?.pratiche_escluse) || 0;
          }
        });
        setExcludedMeta({
          byIssuerId,
          senzaCreditore: Number(excluded.senza_creditore) || 0
        });
      } else {
        setExcludedMeta({ byIssuerId: {}, senzaCreditore: 0 });
      }
    } catch (error) {
      console.error('Error fetching carichi di lavoro:', error);
      toast.error('Errore nel caricamento dei carichi di lavoro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ dateOverride: referenceDate });
    api
      .get('/issuer')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        list.sort((a, b) => {
          const nameA = String(a?.name || '').trim().toLowerCase();
          const nameB = String(b?.name || '').trim().toLowerCase();
          return nameA.localeCompare(nameB, 'it');
        });
        setIssuerOptions(list);
      })
      .catch((error) => {
        console.error('Error fetching issuer options:', error);
        toast.error('Errore nel caricamento dei creditori');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchData({
        dateOverride: referenceDate,
        excludeIssuerIdsOverride: excludeIssuers.map((issuer) => issuer.id),
        legalSearchOverride: search
      });
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const enrichedRows = useMemo(() => {
    return (rows || []).map((row) => {
      const praticheTotale = Number(row?.pratiche_totale) || 0;
      const praticheChiuseTotale = Number(row?.pratiche_chiuse_totale) || 0;
      const praticheRecessiTotale = Number(row?.pratiche_recessi_totale) || 0;
      const clientiTotale = Number(row?.clienti_totale) || 0;
      const clientiChiusiTotale = Number(row?.clienti_chiusi_totale) || 0;
      const clientiRecessi = Number(row?.clienti_recessi_totale) || 0;
      const praticheGestite = Math.max(0, praticheTotale - praticheRecessiTotale - praticheChiuseTotale);
      const clientiGestiti = Math.max(0, clientiTotale - clientiRecessi - clientiChiusiTotale);
      return { ...row, pratiche_gestite: praticheGestite, clienti_gestiti: clientiGestiti };
    });
  }, [rows]);

  const filteredRows = useMemo(() => enrichedRows, [enrichedRows]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA == null) return 1;
      if (valueB == null) return -1;
      if (
        orderBy === 'pratiche_totale' ||
        orderBy === 'pratiche_chiuse_totale' ||
        orderBy === 'pratiche_recessi_totale' ||
        orderBy === 'pratiche_gestite' ||
        orderBy === 'clienti_totale' ||
        orderBy === 'clienti_chiusi_totale' ||
        orderBy === 'clienti_gestiti' ||
        orderBy === 'recessi_totale' ||
        orderBy === 'clienti_recessi_totale' ||
        orderBy === 'pratiche_percentuale'
      ) {
        return order === 'asc' ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredRows, orderBy, order]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Dialog open={introOpen} onClose={() => setIntroOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Carichi per Legale</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Calendar size={18} color="#2563eb" />
            <Typography>
              Questa pagina mostra la distribuzione dei carichi per referente legale fino alla data selezionata.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#16a34a" />
              <Typography variant="body2" color="text.secondary">
                Sono incluse tutte le pratiche assegnate fino alla data indicata, indipendentemente dallo stato.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#2563eb" />
              <Typography variant="body2" color="text.secondary">
                Sono conteggiate solo pratiche con referente legale esistente e assegnazione valida (data assegnazione ≤ data selezionata).
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#f59e0b" />
              <Typography variant="body2" color="text.secondary">
                “Pratiche gestite” = Totale pratiche − pratiche recessi − pratiche chiuse.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#10b981" />
              <Typography variant="body2" color="text.secondary">
                Recesso: dossier con status = failed e motivazione_fallimento ∈ (lasciare, recesso_nei_tempi, perseguire).
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#4f46e5" />
              <Typography variant="body2" color="text.secondary">
                “Clienti chiusi” = clienti per cui tutte le pratiche assegnate fino alla data risultano closed (altrimenti restano da gestire).
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0f172a" />
              <Typography variant="body2" color="text.secondary">
                “Clienti gestiti” = Totale clienti − clienti recessi − clienti chiusi.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0ea5e9" />
              <Typography variant="body2" color="text.secondary">
                Puoi escludere uno o più creditori: quando il filtro è attivo, non vengono conteggiate anche le pratiche senza creditore.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#4f46e5" />
              <Typography variant="body2" color="text.secondary">
                Clicca una riga per il dettaglio e usa le tab: “Distribuzione creditori” e “Dettaglio recessi”.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0f172a" />
              <Typography variant="body2" color="text.secondary">
                Il bottone “Report PDF” genera il report e lo rende disponibile nella campanella.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIntroOpen(false)}>
            Ho capito
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Genera report PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleziona il livello di dettaglio del report. Il report include le metriche della tabella (pratiche chiuse, recessi, clienti chiusi, ecc.) ed è disponibile
            nella campanella al termine della generazione.
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={reportDetailMode}
              onChange={(event) => setReportDetailMode(event.target.value)}
            >
              <FormControlLabel
                value="summary"
                control={<Radio />}
                label={
                  <RadioHelpLabel
                    label="Riepilogo (solo referenti)"
                    help="Genera solo la tabella per referente legale (senza dettaglio per creditore)."
                  />
                }
              />
              <FormControlLabel
                value="issuer"
                control={<Radio />}
                label={
                  <RadioHelpLabel
                    label="Con dettaglio creditori"
                    help="Aggiunge il dettaglio “Distribuzione creditori” per ciascun referente legale."
                  />
                }
              />
              <FormControlLabel
                value="issuer_contabili"
                control={<Radio />}
                label={
                  <RadioHelpLabel
                    label="Con dettaglio creditori + referenti contabili"
                    help="Aggiunge il dettaglio creditori e, per ogni creditore, la distribuzione per referente contabile."
                  />
                }
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setReportDialogOpen(false);
              await handleStartCarichiLavoroReportJob({ detailMode: reportDetailMode });
            }}
            disabled={reportLoading}
          >
            Genera
          </Button>
        </DialogActions>
      </Dialog>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <BarChart3 size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Carichi per Legale
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Distribuzione pratiche per referente legale (data: {formatDate(referenceDate)})
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileText size={16} />}
            disabled={reportLoading}
            onClick={() => setReportDialogOpen(true)}
          >
            Report PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshCcw size={16} />}
            onClick={() => fetchData({ dateOverride: referenceDate })}
            disabled={loading}
          >
            Aggiorna
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            placeholder="Cerca per nominativo o email..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 280, maxWidth: 420, width: '100%' }}
          />
          <TextField
            size="small"
            type="date"
            label="Al"
            InputLabelProps={{ shrink: true }}
            value={referenceDate}
            onChange={(event) => {
              const nextDate = event.target.value;
              setReferenceDate(nextDate);
              setPage(0);
              if (nextDate) {
                fetchData({ dateOverride: nextDate });
              }
            }}
            sx={{ minWidth: 180 }}
          />
          <Autocomplete
            multiple
            size="small"
            options={issuerOptions}
            value={excludeIssuers}
            disableCloseOnSelect
            filterSelectedOptions
            isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
            getOptionLabel={(option) => String(option?.name || option?.id || '')}
            filterOptions={(options, state) => {
              const term = String(state?.inputValue || '')
                .trim()
                .toLowerCase();
              if (!term) return options;
              return options.filter((option) =>
                String(option?.name || option?.id || '')
                  .toLowerCase()
                  .includes(term)
              );
            }}
            onChange={(_event, value) => {
              const next = Array.isArray(value) ? value : [];
              setExcludeIssuers(next);
              setPage(0);
              fetchData({
                dateOverride: referenceDate,
                excludeIssuerIdsOverride: next.map((issuer) => issuer.id)
              });
            }}
            renderInput={(params) => <TextField {...params} label="Escludi creditori" placeholder="Cerca..." />}
            renderTags={(value, getTagProps) => {
              const tags = value.map((option, index) => {
                const id = Number(option?.id);
                const excludedCount = Number.isFinite(id) ? excludedMeta.byIssuerId[id] : 0;
                const labelBase = String(option?.name || option?.id || '');
                const label = excludedCount > 0 ? `${labelBase} (${excludedCount} escluse)` : labelBase;
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={String(option?.id ?? index)}
                    size="small"
                    label={label}
                    color="success"
                    variant="outlined"
                  />
                );
              });
              if (excludedMeta.senzaCreditore > 0) {
                tags.push(
                  <Chip
                    key="senza-creditore"
                    size="small"
                    label={`Senza creditore (${excludedMeta.senzaCreditore} escluse)`}
                    color="success"
                    variant="outlined"
                  />
                );
              }
              return tags;
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li {...optionProps} key={option?.id ?? key}>
                  <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                  {String(option?.name || option?.id || '')}
                </li>
              );
            }}
            sx={{ minWidth: 320, maxWidth: 520, width: '100%' }}
          />
          {excludeIssuers.length > 0 && (
            <Chip
              size="small"
              label={`Creditori esclusi: ${excludeIssuers.length}`}
              color="success"
              variant="outlined"
              onDelete={() => {
                setExcludeIssuers([]);
                setPage(0);
                fetchData({
                  dateOverride: referenceDate,
                  excludeIssuerIdsOverride: []
                });
              }}
            />
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell sortDirection={orderBy === 'name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    <HeaderHelp label="Referente legale" help="Referente legale associato alle pratiche (assegnate entro la data di riferimento)." />
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'last_assignment_date' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'last_assignment_date'}
                    direction={orderBy === 'last_assignment_date' ? order : 'desc'}
                    onClick={() => handleRequestSort('last_assignment_date')}
                  >
                    <HeaderHelp label="Ultima assegnazione" help="Data più recente di assegnazione legale (≤ data di riferimento)." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'pratiche_totale' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'pratiche_totale'}
                    direction={orderBy === 'pratiche_totale' ? order : 'desc'}
                    onClick={() => handleRequestSort('pratiche_totale')}
                  >
                    <HeaderHelp label="Totale Pratiche" help="Numero di pratiche assegnate al legale fino alla data indicata (indipendentemente dallo stato)." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'pratiche_chiuse_totale' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'pratiche_chiuse_totale'}
                    direction={orderBy === 'pratiche_chiuse_totale' ? order : 'desc'}
                    onClick={() => handleRequestSort('pratiche_chiuse_totale')}
                  >
                    <HeaderHelp label="Pratiche chiuse" help="Pratiche con status = closed tra quelle assegnate fino alla data indicata." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'pratiche_recessi_totale' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'pratiche_recessi_totale'}
                    direction={orderBy === 'pratiche_recessi_totale' ? order : 'desc'}
                    onClick={() => handleRequestSort('pratiche_recessi_totale')}
                  >
                    <HeaderHelp label="Pratiche recessi" help="Pratiche collegate a contratti/dossier in recesso (dossier failed + motivazione_fallimento: lasciare, recesso_nei_tempi, perseguire)." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'pratiche_gestite' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'pratiche_gestite'}
                    direction={orderBy === 'pratiche_gestite' ? order : 'desc'}
                    onClick={() => handleRequestSort('pratiche_gestite')}
                  >
                    <HeaderHelp label="Pratiche gestite" help="Totale pratiche − pratiche recessi − pratiche chiuse." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'clienti_totale' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'clienti_totale'}
                    direction={orderBy === 'clienti_totale' ? order : 'desc'}
                    onClick={() => handleRequestSort('clienti_totale')}
                  >
                    <HeaderHelp label="Totale Clienti" help="Clienti distinti collegati alle pratiche assegnate fino alla data indicata." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'clienti_chiusi_totale' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'clienti_chiusi_totale'}
                    direction={orderBy === 'clienti_chiusi_totale' ? order : 'desc'}
                    onClick={() => handleRequestSort('clienti_chiusi_totale')}
                  >
                    <HeaderHelp label="Clienti chiusi" help="Clienti distinti per cui tutte le pratiche assegnate fino alla data indicata risultano closed (se esiste una pratica non closed, il cliente non è chiuso)." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'clienti_recessi_totale' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'clienti_recessi_totale'}
                    direction={orderBy === 'clienti_recessi_totale' ? order : 'desc'}
                    onClick={() => handleRequestSort('clienti_recessi_totale')}
                  >
                    <HeaderHelp label="Clienti recessi" help="Clienti distinti con almeno un contratto/dossier in recesso (dossier failed + motivazione_fallimento: lasciare, recesso_nei_tempi, perseguire)." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'clienti_gestiti' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'clienti_gestiti'}
                    direction={orderBy === 'clienti_gestiti' ? order : 'desc'}
                    onClick={() => handleRequestSort('clienti_gestiti')}
                  >
                    <HeaderHelp label="Clienti gestiti" help="Totale clienti − clienti recessi − clienti chiusi." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'recessi_totale' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'recessi_totale'}
                    direction={orderBy === 'recessi_totale' ? order : 'desc'}
                    onClick={() => handleRequestSort('recessi_totale')}
                  >
                    <HeaderHelp label="Totale Recessi" help="Contratti/dossier distinti in recesso (dossier failed + motivazione_fallimento: lasciare, recesso_nei_tempi, perseguire)." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'pratiche_percentuale' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'pratiche_percentuale'}
                    direction={orderBy === 'pratiche_percentuale' ? order : 'desc'}
                    onClick={() => handleRequestSort('pratiche_percentuale')}
                  >
                    <HeaderHelp label="% sul totale" help="% del totale pratiche complessive (sulla lista filtrata)." />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <HeaderHelp label="Azioni" help="Scarica il PDF di dettaglio per il singolo legale." />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">Nessun dato disponibile</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const rawPercent = Number(row.pratiche_percentuale);
                    const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                    const clampedPercent = Math.max(0, Math.min(100, safePercent));
                    const legalId = Number(row.legal_id);
                    const isExpanded = expandedLegalId === legalId;
                    const issuerItems = issuerDistributionByLegalId[legalId] || [];
                    const issuerLoading = Boolean(issuerDistributionLoadingByLegalId[legalId]);
                    const recessiItems = recessiMotivazioniByLegalId[legalId] || [];
                    const recessiLoading = Boolean(recessiMotivazioniLoadingByLegalId[legalId]);
                    const recessiTipologieItems = recessiTipologieByLegalId[legalId] || [];
                    const recessiTipologieLoading = Boolean(recessiTipologieLoadingByLegalId[legalId]);
                    return (
                      <Fragment key={row.legal_id}>
                        <TableRow
                          hover
                          onClick={async () => {
                            if (!Number.isFinite(legalId) || legalId <= 0) return;
                            const nextExpanded = expandedLegalId === legalId ? null : legalId;
                            setExpandedLegalId(nextExpanded);
                            setDetailTab('issuer');
                            setRecessiTab('motivazioni');
                            setExpandedIssuerKey(null);
                            setExpandedMotivazioneKey(null);
                            setExpandedTipologiaKey(null);
                            if (nextExpanded && !issuerDistributionByLegalId[legalId]) {
                              await fetchIssuerDistribution(legalId);
                            }
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ minWidth: 320 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={async (event) => {
                                  event.stopPropagation();
                                  if (!Number.isFinite(legalId) || legalId <= 0) return;
                                  const nextExpanded = expandedLegalId === legalId ? null : legalId;
                                  setExpandedLegalId(nextExpanded);
                                  setDetailTab('issuer');
                                  setRecessiTab('motivazioni');
                                  setExpandedIssuerKey(null);
                                  setExpandedMotivazioneKey(null);
                                  setExpandedTipologiaKey(null);
                                  if (nextExpanded && !issuerDistributionByLegalId[legalId]) {
                                    await fetchIssuerDistribution(legalId);
                                  }
                                }}
                              >
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </IconButton>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Box
                                  sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: '50%',
                                    bgcolor: '#eef2ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mt: 0.2
                                  }}
                                >
                                  <User size={18} color="#2563eb" />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                    {row.name || '-'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Mail size={14} color="#64748b" />
                                    <Typography variant="body2" color="text.secondary">
                                      {row.email || '-'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(row.last_assignment_date)}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(245, 158, 11, 0.35)',
                                backgroundColor: 'rgba(245, 158, 11, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: 0.2 }}>
                                {Number(row.pratiche_totale) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(148, 163, 184, 0.35)',
                                backgroundColor: 'rgba(148, 163, 184, 0.06)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', letterSpacing: 0.2 }}>
                                {Number(row.pratiche_chiuse_totale) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(220, 38, 38, 0.22)',
                                backgroundColor: 'rgba(220, 38, 38, 0.03)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', letterSpacing: 0.2 }}>
                                {Number(row.pratiche_recessi_totale) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(22, 163, 74, 0.22)',
                                backgroundColor: 'rgba(22, 163, 74, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a', letterSpacing: 0.2 }}>
                                {Number(row.pratiche_gestite) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(37, 99, 235, 0.25)',
                                backgroundColor: 'rgba(37, 99, 235, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', letterSpacing: 0.2 }}>
                                {Number(row.clienti_totale) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(148, 163, 184, 0.35)',
                                backgroundColor: 'rgba(148, 163, 184, 0.06)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', letterSpacing: 0.2 }}>
                                {Number(row.clienti_chiusi_totale) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(220, 38, 38, 0.22)',
                                backgroundColor: 'rgba(220, 38, 38, 0.03)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', letterSpacing: 0.2 }}>
                                {Number(row.clienti_recessi_totale) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(22, 163, 74, 0.22)',
                                backgroundColor: 'rgba(22, 163, 74, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a', letterSpacing: 0.2 }}>
                                {Number(row.clienti_gestiti) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(239, 68, 68, 0.28)',
                                backgroundColor: 'rgba(239, 68, 68, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#ef4444', letterSpacing: 0.2 }}>
                                {Number(row.recessi_totale) || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <CircularProgress
                                  variant="determinate"
                                  value={100}
                                  size={36}
                                  thickness={6}
                                  sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                                />
                                <CircularProgress
                                  variant="determinate"
                                  value={clampedPercent}
                                  size={36}
                                  thickness={6}
                                  sx={{ position: 'absolute', left: 0 }}
                                  color="warning"
                                />
                                <Box
                                  sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                    {safePercent.toFixed(0)}%
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="PDF dettaglio legale" arrow>
                              <IconButton
                                size="small"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await handleStartLegalDetailReportJob(row);
                                }}
                                sx={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: '12px' }}
                              >
                                <FileText size={18} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={13} sx={{ p: 0, borderBottom: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, pt: 1.5, bgcolor: 'rgba(145, 158, 171, 0.06)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Dettaglio
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Totale pratiche: {Number(row.pratiche_totale) || 0} · Pratiche chiuse: {Number(row.pratiche_chiuse_totale) || 0} · Pratiche recessi:{' '}
                                    {Number(row.pratiche_recessi_totale) || 0} · Pratiche gestite: {Number(row.pratiche_gestite) || 0} · Totale clienti:{' '}
                                    {Number(row.clienti_totale) || 0} · Clienti chiusi: {Number(row.clienti_chiusi_totale) || 0} · Clienti recessi:{' '}
                                    {Number(row.clienti_recessi_totale) || 0} · Clienti gestiti: {Number(row.clienti_gestiti) || 0} · Totale recessi:{' '}
                                    {Number(row.recessi_totale) || 0}
                                  </Typography>
                                </Box>

                                <Tabs
                                  value={detailTab}
                                  onChange={async (_event, nextValue) => {
                                    const selected = String(nextValue || 'issuer');
                                    setDetailTab(selected);
                                    if (selected === 'issuer') {
                                      setExpandedMotivazioneKey(null);
                                      setExpandedTipologiaKey(null);
                                      if (!issuerDistributionByLegalId[legalId]) {
                                        await fetchIssuerDistribution(legalId);
                                      }
                                      return;
                                    }
                                    if (selected === 'recessi') {
                                      setExpandedIssuerKey(null);
                                      setExpandedMotivazioneKey(null);
                                      setExpandedTipologiaKey(null);
                                      if (recessiTab === 'tipologie') {
                                        if (!recessiTipologieByLegalId[legalId]) {
                                          await fetchRecessiTipologieDistribution(legalId);
                                        }
                                        return;
                                      }
                                      if (!recessiMotivazioniByLegalId[legalId]) {
                                        await fetchRecessiMotivazioniDistribution(legalId);
                                      }
                                    }
                                  }}
                                  sx={{ mt: 1, minHeight: 40 }}
                                >
                                  <Tab value="issuer" label="Distribuzione creditori" sx={{ minHeight: 40 }} />
                                  <Tab value="recessi" label="Dettaglio recessi" sx={{ minHeight: 40 }} />
                                </Tabs>

                                {detailTab === 'issuer' ? (
                                  issuerLoading ? (
                                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                                      <CircularProgress size={20} />
                                    </Box>
                                  ) : issuerItems.length === 0 ? (
                                    <Box sx={{ py: 2 }}>
                                      <Typography variant="body2" color="text.secondary">
                                        Nessun dettaglio disponibile
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Table size="small" sx={{ mt: 1 }}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Creditore</TableCell>
                                          <TableCell align="right">Pratiche</TableCell>
                                          <TableCell align="right">Clienti</TableCell>
                                          <TableCell align="right">% pratiche</TableCell>
                                          <TableCell align="right">% clienti</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {issuerItems.map((item) => {
                                          const issuerId = Number(item.issuer_id);
                                          const issuerKey = buildIssuerKey(legalId, issuerId);
                                          const isIssuerExpanded = expandedIssuerKey === issuerKey;
                                          const contabiliItems = contabiliDistributionByIssuerKey[issuerKey] || [];
                                          const contabiliLoading = Boolean(contabiliDistributionLoadingByIssuerKey[issuerKey]);
                                          return (
                                            <Fragment key={`${legalId}-${item.issuer_id}`}>
                                              <TableRow hover sx={{ cursor: 'pointer' }}>
                                                <TableCell sx={{ maxWidth: 520 }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <IconButton
                                                      size="small"
                                                      onClick={async (event) => {
                                                        event.stopPropagation();
                                                        const nextExpanded = expandedIssuerKey === issuerKey ? null : issuerKey;
                                                        setExpandedIssuerKey(nextExpanded);
                                                        if (nextExpanded && !contabiliDistributionByIssuerKey[issuerKey]) {
                                                          await fetchContabiliDistribution(legalId, issuerId);
                                                        }
                                                      }}
                                                    >
                                                      {isIssuerExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </IconButton>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                      {item.issuer_name || '-'}
                                                    </Typography>
                                                  </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography variant="body2">{Number(item.pratiche_totale) || 0}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography variant="body2">{Number(item.clienti_totale) || 0}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography variant="body2">
                                                    {row.pratiche_totale > 0
                                                      ? `${((Number(item.pratiche_totale) || 0) / Number(row.pratiche_totale) * 100).toFixed(0)}%`
                                                      : '0%'}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography variant="body2">
                                                    {row.clienti_totale > 0
                                                      ? `${((Number(item.clienti_totale) || 0) / Number(row.clienti_totale) * 100).toFixed(0)}%`
                                                      : '0%'}
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>

                                              <TableRow>
                                                <TableCell colSpan={5} sx={{ p: 0, borderBottom: 0 }}>
                                                  <Collapse in={isIssuerExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 1.5, pl: 5, bgcolor: 'rgba(145, 158, 171, 0.06)' }}>
                                                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                        Distribuzione per referente contabile (dossier)
                                                      </Typography>
                                                      {contabiliLoading ? (
                                                        <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>
                                                          <CircularProgress size={18} />
                                                        </Box>
                                                      ) : contabiliItems.length === 0 ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                          Nessun dettaglio disponibile
                                                        </Typography>
                                                      ) : (
                                                        <Table size="small">
                                                          <TableHead>
                                                            <TableRow>
                                                              <TableCell>Referente contabile</TableCell>
                                                              <TableCell align="right">Pratiche</TableCell>
                                                              <TableCell align="right">Clienti</TableCell>
                                                            </TableRow>
                                                          </TableHead>
                                                          <TableBody>
                                                            {contabiliItems.map((c) => (
                                                              <TableRow key={`${issuerKey}-${c.contabile_id}`}>
                                                                <TableCell>
                                                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {c.contabile_name || '-'}
                                                                  </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                  <Typography variant="body2">{Number(c.pratiche_totale) || 0}</Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                  <Typography variant="body2">{Number(c.clienti_totale) || 0}</Typography>
                                                                </TableCell>
                                                              </TableRow>
                                                            ))}
                                                          </TableBody>
                                                        </Table>
                                                      )}
                                                    </Box>
                                                  </Collapse>
                                                </TableCell>
                                              </TableRow>
                                            </Fragment>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  )
                                ) : detailTab === 'recessi' ? (
                                  <Box>
                                    <Tabs
                                      value={recessiTab}
                                      onChange={async (_event, nextValue) => {
                                        const selected = String(nextValue || 'motivazioni');
                                        setRecessiTab(selected);
                                        setExpandedMotivazioneKey(null);
                                        setExpandedTipologiaKey(null);
                                        if (selected === 'tipologie') {
                                          if (!recessiTipologieByLegalId[legalId]) {
                                            await fetchRecessiTipologieDistribution(legalId);
                                          }
                                          return;
                                        }
                                        if (!recessiMotivazioniByLegalId[legalId]) {
                                          await fetchRecessiMotivazioniDistribution(legalId);
                                        }
                                      }}
                                      sx={{ mt: 1, minHeight: 40 }}
                                    >
                                      <Tab value="motivazioni" label="Per motivazione" sx={{ minHeight: 40 }} />
                                      <Tab value="tipologie" label="Per tipologia pratica" sx={{ minHeight: 40 }} />
                                    </Tabs>

                                    {recessiTab === 'tipologie' ? (
                                      recessiTipologieLoading ? (
                                        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                                          <CircularProgress size={20} />
                                        </Box>
                                      ) : recessiTipologieItems.length === 0 ? (
                                        <Box sx={{ py: 2 }}>
                                          <Typography variant="body2" color="text.secondary">
                                            Nessun dettaglio disponibile
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <Table size="small" sx={{ mt: 1 }}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Tipologia pratica</TableCell>
                                              <TableCell align="right">Contratti</TableCell>
                                              <TableCell align="right">Pratiche</TableCell>
                                              <TableCell align="right">Clienti</TableCell>
                                              <TableCell align="right">Importo</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {recessiTipologieItems.map((item) => {
                                              const categoryId = Number(item?.category_id) || 0;
                                              const tipologiaKey = buildTipologiaKey(legalId, categoryId);
                                              const isTipologiaExpanded = expandedTipologiaKey === tipologiaKey;
                                              const detailsItems = recessiTipologieDetailsByKey[tipologiaKey] || [];
                                              const detailsLoading = Boolean(recessiTipologieDetailsLoadingByKey[tipologiaKey]);
                                              return (
                                                <Fragment key={tipologiaKey}>
                                                  <TableRow hover>
                                                    <TableCell sx={{ maxWidth: 520 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <IconButton
                                                          size="small"
                                                          disabled={(Number(item?.contratti_totale) || 0) <= 0}
                                                          onClick={async (event) => {
                                                            event.stopPropagation();
                                                            setExpandedMotivazioneKey(null);
                                                            const nextExpanded = expandedTipologiaKey === tipologiaKey ? null : tipologiaKey;
                                                            setExpandedTipologiaKey(nextExpanded);
                                                            if (nextExpanded && !recessiTipologieDetailsByKey[tipologiaKey]) {
                                                              await fetchRecessiTipologieDetails(legalId, categoryId);
                                                            }
                                                          }}
                                                        >
                                                          {isTipologiaExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                        </IconButton>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                          {item?.category_name || 'Non assegnata'}
                                                        </Typography>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography variant="body2">{Number(item?.contratti_totale) || 0}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography variant="body2">{Number(item?.pratiche_totale) || 0}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography variant="body2">{Number(item?.clienti_totale) || 0}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                      <Typography variant="body2">
                                                        {item?.importo_totale != null
                                                          ? Number(item.importo_totale).toLocaleString('it-IT', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                          })
                                                          : '-'}
                                                      </Typography>
                                                    </TableCell>
                                                  </TableRow>

                                                  <TableRow>
                                                    <TableCell colSpan={5} sx={{ p: 0, borderBottom: 0 }}>
                                                      <Collapse in={isTipologiaExpanded} timeout="auto" unmountOnExit>
                                                        <Box sx={{ p: 1.5, pl: 5, bgcolor: 'rgba(145, 158, 171, 0.06)' }}>
                                                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                            Dettaglio contratti in recesso
                                                          </Typography>
                                                          {detailsLoading ? (
                                                            <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>
                                                              <CircularProgress size={18} />
                                                            </Box>
                                                          ) : detailsItems.length === 0 ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                              Nessun dettaglio disponibile
                                                            </Typography>
                                                          ) : (
                                                            <Table size="small">
                                                              <TableHead>
                                                                <TableRow>
                                                                  <TableCell>Cliente</TableCell>
                                                                  <TableCell>Contratto</TableCell>
                                                                  <TableCell align="right">Pratiche</TableCell>
                                                                  <TableCell>Data conferimento</TableCell>
                                                                  <TableCell align="right">Importo</TableCell>
                                                                </TableRow>
                                                              </TableHead>
                                                              <TableBody>
                                                                {detailsItems.map((d) => (
                                                                  <TableRow key={`${tipologiaKey}-${d.dossier_id}`}>
                                                                    <TableCell sx={{ maxWidth: 420 }}>
                                                                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                                        {d.customer_name || '-'}
                                                                      </Typography>
                                                                      <Typography variant="caption" color="text.secondary">
                                                                        {(d.customer_phone || '-')}{d.customer_email ? ` · ${d.customer_email}` : ''}
                                                                      </Typography>
                                                                    </TableCell>
                                                                    <TableCell sx={{ maxWidth: 320 }}>
                                                                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                                        {d.dossier_reference || '-'}
                                                                      </Typography>
                                                                      <Typography variant="caption" color="text.secondary">
                                                                        {d.tipo_contratto || '-'}
                                                                      </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                      <Typography variant="body2">{Number(d.pratiche_totale) || 0}</Typography>
                                                                    </TableCell>
                                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                                      {formatDate(d.dossier_data_conferimento)}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                                      {d.importo_totale != null
                                                                        ? Number(d.importo_totale).toLocaleString('it-IT', {
                                                                          minimumFractionDigits: 2,
                                                                          maximumFractionDigits: 2
                                                                        })
                                                                        : '-'}
                                                                    </TableCell>
                                                                  </TableRow>
                                                                ))}
                                                              </TableBody>
                                                            </Table>
                                                          )}
                                                        </Box>
                                                      </Collapse>
                                                    </TableCell>
                                                  </TableRow>
                                                </Fragment>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      )
                                    ) : recessiLoading ? (
                                      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                                        <CircularProgress size={20} />
                                      </Box>
                                    ) : recessiItems.length === 0 ? (
                                      <Box sx={{ py: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          Nessun dettaglio disponibile
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Table size="small" sx={{ mt: 1 }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Motivazione</TableCell>
                                            <TableCell align="right">Contratti</TableCell>
                                            <TableCell align="right">Pratiche</TableCell>
                                            <TableCell align="right">Clienti</TableCell>
                                            <TableCell align="right">Importo</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {recessiItems.map((item) => {
                                            const motivazione = String(item?.motivazione || '').trim().toLowerCase();
                                            const motivazioneKey = buildMotivazioneKey(legalId, motivazione);
                                            const isMotivazioneExpanded = expandedMotivazioneKey === motivazioneKey;
                                            const detailsItems = recessiMotivazioniDetailsByKey[motivazioneKey] || [];
                                            const detailsLoading = Boolean(recessiMotivazioniDetailsLoadingByKey[motivazioneKey]);
                                            return (
                                              <Fragment key={motivazioneKey}>
                                                <TableRow hover>
                                                  <TableCell sx={{ maxWidth: 520 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                      <IconButton
                                                        size="small"
                                                        disabled={(Number(item?.contratti_totale) || 0) <= 0}
                                                        onClick={async (event) => {
                                                          event.stopPropagation();
                                                          setExpandedTipologiaKey(null);
                                                          const nextExpanded = expandedMotivazioneKey === motivazioneKey ? null : motivazioneKey;
                                                          setExpandedMotivazioneKey(nextExpanded);
                                                          if (nextExpanded && !recessiMotivazioniDetailsByKey[motivazioneKey]) {
                                                            await fetchRecessiMotivazioniDetails(legalId, motivazione);
                                                          }
                                                        }}
                                                      >
                                                        {isMotivazioneExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                      </IconButton>
                                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {motivazioneLabel(motivazione)}
                                                      </Typography>
                                                    </Box>
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    <Typography variant="body2">{Number(item?.contratti_totale) || 0}</Typography>
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    <Typography variant="body2">{Number(item?.pratiche_totale) || 0}</Typography>
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    <Typography variant="body2">{Number(item?.clienti_totale) || 0}</Typography>
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Typography variant="body2">
                                                      {item?.importo_totale != null
                                                        ? Number(item.importo_totale).toLocaleString('it-IT', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2
                                                        })
                                                        : '-'}
                                                    </Typography>
                                                  </TableCell>
                                                </TableRow>

                                                <TableRow>
                                                  <TableCell colSpan={5} sx={{ p: 0, borderBottom: 0 }}>
                                                    <Collapse in={isMotivazioneExpanded} timeout="auto" unmountOnExit>
                                                      <Box sx={{ p: 1.5, pl: 5, bgcolor: 'rgba(145, 158, 171, 0.06)' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                          Dettaglio contratti in recesso
                                                        </Typography>
                                                        {detailsLoading ? (
                                                          <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>
                                                            <CircularProgress size={18} />
                                                          </Box>
                                                        ) : detailsItems.length === 0 ? (
                                                          <Typography variant="body2" color="text.secondary">
                                                            Nessun dettaglio disponibile
                                                          </Typography>
                                                        ) : (
                                                          <Table size="small">
                                                            <TableHead>
                                                              <TableRow>
                                                                <TableCell>Cliente</TableCell>
                                                                <TableCell>Contratto</TableCell>
                                                                <TableCell align="right">Pratiche</TableCell>
                                                                <TableCell>Data conferimento</TableCell>
                                                                <TableCell align="right">Importo</TableCell>
                                                              </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                              {detailsItems.map((d) => (
                                                                <TableRow key={`${motivazioneKey}-${d.dossier_id}`}>
                                                                  <TableCell sx={{ maxWidth: 420 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                                      {d.customer_name || '-'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                      {(d.customer_phone || '-')}{d.customer_email ? ` · ${d.customer_email}` : ''}
                                                                    </Typography>
                                                                  </TableCell>
                                                                  <TableCell sx={{ maxWidth: 320 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                                      {d.dossier_reference || '-'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                      {d.tipo_contratto || '-'}
                                                                    </Typography>
                                                                  </TableCell>
                                                                  <TableCell align="right">
                                                                    <Typography variant="body2">{Number(d.pratiche_totale) || 0}</Typography>
                                                                  </TableCell>
                                                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                                    {formatDate(d.dossier_data_conferimento)}
                                                                  </TableCell>
                                                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                                    {d.importo_totale != null
                                                                      ? Number(d.importo_totale).toLocaleString('it-IT', {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2
                                                                      })
                                                                      : '-'}
                                                                  </TableCell>
                                                                </TableRow>
                                                              ))}
                                                            </TableBody>
                                                          </Table>
                                                        )}
                                                      </Box>
                                                    </Collapse>
                                                  </TableCell>
                                                </TableRow>
                                              </Fragment>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Box>
                                ) : null}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
